<template>
    <div class="xl:w-3/4">
        <div class="pt-2 pb-1 text-2xl font-bold">Items</div>

        <div class="rounded-t overflow-hidden border border-b-0">
            <table class="table w-full">
                <thead>
                    <tr>
                        <th v-if="!isCustomer" class="text-left whitespace-nowrap">Item #</th>
                        <th class="text-left w-1/2 md:w-1/3">Product</th>
                        <th class="hidden md:table-cell text-left w-1/3">Notes</th>
                        <th class="text-center">Price</th>
                        <th class="text-center">Quantity</th>
                        <th class="hidden sm:table-cell text-center">Tax</th>
                        <th v-if="hasPromo" class="hidden md:table-cell text-right whitespace-nowrap">Discount</th>
                        <th class="hidden md:table-cell text-right whitespace-nowrap">Total</th>
                        <th></th>
                    </tr>
                    <tr v-if="$page.props.errors.order_items">
                        <td colspan="6" class="bg-white">
                            <jet-input-error :message="$page.props.errors.order_items" class="mt-1" />
                        </td>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="(item, index) in form.order_items" :key="index">
                        <td v-if="!isCustomer">{{ item.product_id }}</td>

                        <td>
                            {{ item.product.title }}
                            <span v-if="item.product.description"> - {{ item.product.description }}</span>
                            <span v-if="item.product.size"> - {{ item.product.size }}</span>
                        </td>

                        <td class="hidden md:table-cell">
                            <template v-if="isAdmin">
                                <jet-input
                                    type="text"
                                    class="notes text-left w-full"
                                    v-model="item.notes"
                                    maxlength="191"
                                />
                                <jet-input-error :message="$page.props.errors[`order_items.${index}.notes`]" class="mt-1" />
                            </template>

                            <p v-else class="text-left text-muted">{{ item.notes }}</p>
                        </td>

                        <td class="text-center">
                            <template v-if="!isCustomer">
                                <jet-input
                                    type="number"
                                    class="price text-right"
                                    min="0"
                                    step="0.01"
                                    v-model="item.price"
                                    v-numbers
                                    @keydown.enter.prevent
                                />
                                <jet-input-error
                                    :message="$page.props.errors[`order_items.${index}.price`]"
                                    class="mt-1"
                                />
                            </template>

                            <template v-else>
                                <div class="text-right">
                                    {{ toCurrency(item.price) }}
                                </div>
                            </template>
                        </td>

                        <td class="text-center">
                            <jet-input-quantity
                                v-model.number="item.quantity"
                                class="quantity text-center"
                                min="0"
                                :step="productQuantityStep(item.product)"
                                @input="onQuantityChange(index)"
                            />
                            <jet-input-error :message="$page.props.errors[`order_items.${index}.quantity`]" class="mt-1" />
                        </td>

                        <td class="hidden sm:table-cell text-center">
                            <img v-if="item.tax" src="/images/icons/check.svg" width="20" height="20" class="mx-auto" />
                            <img v-else src="/images/icons/x.svg" width="20" height="20" class="mx-auto" />
                        </td>

                        <td v-if="hasPromo" class="hidden md:table-cell text-right">{{ lineDiscountAmt(item) > 0 ? toCurrency(lineDiscountAmt(item)) : '' }}</td>
                        <td class="hidden md:table-cell text-right">{{ toCurrency(lineTotal(item)) }}</td>

                        <td class="text-center" style="width: 56px;">
                            <jet-secondary-button type="button" @click="removeItem(index)">
                                <img src="/images/icons/trash_w.svg" width="16" height="16" alt="Delete" />
                            </jet-secondary-button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="flex flex-col md:flex-row justify-between">
            <div class="flex gap-x-2 pt-2 flex-1">
                <jet-input-quantity
                    v-model.number="qtyToAdd"
                    class="quantity text-center h-7"
                    placeholder="Qty"
                    :step="0.5"
                    :ref="'qtyToAdd'"
                    @keydown.enter.prevent
                />
                <div class="flex-1">
                    <ProductMultiselect
                        class="multi-sm h-7 text-base"
                        :product-options="productOptions"
                        :loading="loadingProducts"
                        placeholder="Select Product"
                        @select="onProductSelected"
                        ref="itemMultiselect"
                    >
                        <template #option="{ option }">
                            <span
                                :class="{
                                    'text-gray-400': option.enabled === false || option.available === 0
                                }"
                            >
                                <div v-if="option.available === 0">
                                    <span class="text-xs text-white bg-red-400 rounded py-1 px-2">
                                        Out of Stock
                                    </span>
                                </div>

                                <span v-if="!isCustomer">{{ option.id }} - </span>{{ option.title }}
                                <span class="text-xs italic text-red" v-if="option.special_order">
                                    (Special Order Item)
                                </span>
                            </span>
                        </template>
                    </ProductMultiselect>
                    <jet-input-error :message="addItemError" class="mt-1" />
                </div>
            </div>

            <div class="mt-3 md:mt-0 text-right md:pr-[40px]">
                <table class="ml-auto">
                    <tbody>
                        <tr>
                            <th class="px-2 py-1 text-right">Subtotal:</th>
                            <td class="px-2 py-1 text-right">{{ toCurrency(subtotal) }}</td>
                        </tr>
                        <tr v-if="!isCustomer && form.override_delivery_fee">
                            <th class="px-2 py-1 text-right whitespace-nowrap">Delivery Fee:</th>
                            <td class="px-2 py-1 text-right">
                                <div class="flex justify-end">
                                    <div class="py-1 px-3 border border-r-0 rounded-r-none rounded-l">$</div>
                                    <jet-input type="text" class="rounded-l-none w-24 text-right" v-model="form.delivery_fee"/>
                                </div>
                            </td>
                        </tr>
                        <tr v-else-if="isCustomer && form.delivery_fee">
                            <th class="px-2 py-1 text-right whitespace-nowrap">Delivery Fee:</th>
                            <td class="px-2 py-1 text-right">{{ toCurrency(form.delivery_fee) }}</td>
                        </tr>
                        <tr>
                            <th class="px-2 py-1 text-right">Tax:</th>
                            <td class="px-2 py-1 text-right">{{ toCurrency(itemTax) }}</td>
                        </tr>
                        <tr>
                            <th class="px-2 py-1 text-right">Total:</th>
                            <td class="px-2 py-1 text-right font-bold">{{ toCurrency(total) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div v-if="!isCustomer" class="flex justify-end items-center mt-2 md:pr-[40px]">
            <jet-input
                type="checkbox"
                v-model:checked="form.override_delivery_fee"
                class="h-5 w-5 mr-2"
                @click="toggleDeliveryFeeOverride()"
            />
            <jet-label
                for="override_delivery_fee"
                value="Override auto-calculated delivery fee on order generation"
                class="text-sm text-gray-700"
            />
        </div>
    </div>
</template>

<script>
import JetInput from '@/Jetstream/Input'
import JetInputQuantity from '@/Jetstream/InputQuantity'
import JetInputError from '@/Jetstream/InputError'
import JetLabel from '@/Jetstream/Label';
import JetSecondaryButton from '@/Jetstream/SecondaryButton'
import { usePage } from '@inertiajs/inertia-vue3'
import UseUserAuth from '@/Composables/UseUserAuth'
import { getLineDiscountedTotal, lineDiscountAmount } from '@/Composables/useOrderTotals'
import ProductMultiselect from '@/Shared/ProductMultiSelect.vue'
import {
    productQuantityStep,
    normalizeQuantityForProduct,
    shouldApplySplitCaseSurcharge as productShouldApplySplitCaseSurcharge,
} from '@/Composables/UseProductQuantity'

export default {
    components: {
        JetInput,
        JetInputQuantity,
        JetInputError,
        JetSecondaryButton,
        ProductMultiselect,
        JetLabel,
    },

    props: ['form', 'customers'],

    setup() {
        const { isAdmin, isCustomer } = UseUserAuth(usePage().props.value.user)

        return { isAdmin, isCustomer, productQuantityStep }
    },

    data() {
        return {
            products: [],
            itemToAdd: null,
            qtyToAdd: null,
            addItemError: null,
            loadingProducts: false,
        }
    },

    computed: {
        productOptions() {
            let products = this.products;
            return products.map(p => {
                let title = p.title;

                if (p.description) title += ' - ' + p.description;
                if (p.size) title += ' - ' + p.size;

                return {
                    id: p.id,
                    title: title,
                    special_order: p.special_order,
                    special_order_lead_hours: p.special_order_lead_hours,
                    category_id: p.category_id,
                    available: p.available,
                    enabled: p.enabled,
                }
            });
        },

        subtotal() {
            return this.roundDecimals(this.form.order_items.reduce(function(accumulator, currentValue){
                return accumulator + (currentValue.quantity * currentValue.price);
            }, 0));
        },

        delivery_fee() {
            return this.form.delivery_fee ? parseFloat(this.form.delivery_fee) : 0;
        },

        itemTax() {
            let taxRatio = 0.13;
            let tax = this.form.order_items.reduce(function(accumulator, currentValue) {
                return accumulator + (currentValue.tax === true ? currentValue.quantity * currentValue.price * taxRatio : 0)
            }, 0)

            return tax;
        },

        total() {
            let total = this.subtotal + this.itemTax + this.delivery_fee;

            return this.roundDecimals(total);
        },

        selectedCustomer() {
            return this.form.customer_id == null ? null : this.customers.find(i => i.id == this.form.customer_id);
        },

        hasPromo() {
            return !!(this.form.applied_promo_code || this.form.promo_code_id);
        },

        orderForTotals() {
            return {
                order_items: this.form.order_items || [],
                delivery_fee: this.form.delivery_fee,
                fee_for_delivery: this.form.delivery_fee,
                promo_code_percent: this.form.promo_code_percent,
                promo_code_amount: this.form.promo_code_amount,
                applied_promo_code: this.form.applied_promo_code,
                promo_code_id: this.form.promo_code_id,
                tax_exempt: this.form.tax_exempt ?? null, // recurring_orders has no tax_exempt column
                customer: this.form.customer || null,
                applied_credit_amount: this.form.applied_credit_amount || 0
            };
        },
    },

    mounted() {
        this.getProducts();
        this.initializeWholesalePricingState();
    },

    methods: {
        getProducts() {
            this.loadingProducts = true;

            const getProductsRoute = this.isCustomer ? route('recurring_orders.get_products') : route('orders.get_products');

            axios.get(getProductsRoute, {
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache',
                    'Expires': '0',
                },
            })
            .then(response => {
                this.products = response.data;

                this.$nextTick(() => {
                    this.loadingProducts = false;
                });
            });
        },

        lineTotal(item) {
            if (this.hasPromo) {
                return getLineDiscountedTotal(this.orderForTotals, item);
            }
            return item.price * item.quantity;
        },

        lineDiscountAmt(item) {
            return lineDiscountAmount(this.orderForTotals, item);
        },

        isSplitCaseQuantity(quantity) {
            const parsedQuantity = Number(quantity);
            return parsedQuantity > 0 && parsedQuantity < 1;
        },

        shouldApplySplitCaseSurcharge(quantity, product) {
            return productShouldApplySplitCaseSurcharge(quantity, this.selectedCustomer, product);
        },

        quantityCrossedSplitCaseThreshold(previousQuantity, newQuantity, product) {
            return this.shouldApplySplitCaseSurcharge(previousQuantity, product) !== this.shouldApplySplitCaseSurcharge(newQuantity, product);
        },

        initializeWholesalePricingState() {
            this.form.order_items.forEach(item => {
                if (item.last_quantity_for_price === undefined) {
                    item.last_quantity_for_price = item.quantity;
                }
            });
        },

        onQuantityChange(index) {
            const item = this.form.order_items[index];
            const basePrice = item.customer_price ?? item.product?.price;
            const splitPrice = item.customer_split_case_price ?? item.product?.split_case_price ?? item.product?.price;
            const previousQuantity = item.last_quantity_for_price ?? item.quantity;

            if ((basePrice != null || splitPrice != null) && this.quantityCrossedSplitCaseThreshold(previousQuantity, item.quantity, item.product)) {
                item.price = this.shouldApplySplitCaseSurcharge(item.quantity, item.product) ? (splitPrice ?? basePrice) : basePrice;
            }

            item.last_quantity_for_price = item.quantity;
            this.form.order_items = [...this.form.order_items];
        },

        recalculateTotals() {
            this.form.order_items = [...this.form.order_items]
        },

        onProductSelected(productId) {
            this.itemToAdd = productId;
            this.handleAddItem();
        },

        removeItem(index) {
            this.form.order_items.splice(index, 1)
        },

        handleAddItem() {
            let product = this.products.find(i => i.id === this.itemToAdd)

            const pricing_route = this.route('pricing.fetch', {
                product: product.id,
                customer: this.form.customer_id,
            });

            axios
                .get(pricing_route)
                .then(response => {
                    if (this.isCustomer && (response.data === null || response.data === '')) {
                        const index = this.products.findIndex(p => p.id === product.id);

                        if (index !== -1) {
                            this.products.splice(index, 1);
                        }

                        this.addItemError = 'Item no longer available';
                    } else {
                        this.qtyToAdd = normalizeQuantityForProduct(this.qtyToAdd, product);

                        let priceResponse = response.data;
                        let quantity = this.qtyToAdd;
                        let price = priceResponse;
                        let customerPrice = null;
                        let customerSplitCasePrice = null;
                        if (priceResponse && typeof priceResponse === 'object' && 'split_case_price' in priceResponse) {
                            customerPrice = priceResponse.price;
                            customerSplitCasePrice = priceResponse.split_case_price;
                            price = this.shouldApplySplitCaseSurcharge(quantity, product) ? priceResponse.split_case_price : priceResponse.price;
                            product.price = priceResponse.price;
                            product.split_case_price = priceResponse.split_case_price;
                        } else if (typeof priceResponse === 'object' && priceResponse !== null) {
                            price = priceResponse.price ?? priceResponse;
                        }
                        this.form.order_items.push({
                            product_id: product.id,
                            product: product,
                            title: product.title,
                            notes: '',
                            price: price,
                            quantity: quantity,
                            tax: product.tax,
                            customer_price: customerPrice,
                            customer_split_case_price: customerSplitCasePrice,
                            last_quantity_for_price: quantity,
                        });

                        this.$nextTick(() => {
                            this.$refs['qtyToAdd'].$el.focus();
                            this.$refs['qtyToAdd'].$el.select();
                            window.scrollTo(0, document.body.scrollHeight);
                        });

                        this.itemToAdd = null
                        this.$refs.itemMultiselect.clear()
                        this.qtyToAdd = null;
                    }
                });
        },

        toggleDeliveryFeeOverride() {
            this.form.override_delivery_fee = !this.form.override_delivery_fee;

            if (!this.form.override_delivery_fee) {
                this.form.delivery_fee = null;
            }

            return this.form.override_delivery_fee;
        },
    },
}
</script>
